import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity, ArrowUpRight, BarChart3, BookOpen, Bot, Check, ChevronsUpDown, CircleUserRound, House, LogOut, Menu, MessageSquareText,
  Newspaper, PanelLeftClose, PanelLeftOpen, Plus, QrCode, Search, Settings, Store, Users, X,
} from 'lucide-react';
import sidebarMark from 'src/assets/branding/auto-appy-mark-256x256.png';
import { useAuth } from 'src/context/AuthContext';
import { useWorkspace } from 'src/workspace/WorkspaceContext';
import { autoAppyAppInitials, autoAppyAppLaunchUrl, autoAppySwitcherApps } from 'src/config/autoAppyApps';
import { autoAppyUrl } from 'src/config/autoAppyUrls';
import { FOOTER_ITEMS, NAV_GROUPS, activeItemId, productMark, productTone } from './gbpNav';
import { Monogram } from './StageUi';

const ICONS = {
  home: House, reviews: MessageSquareText, posts: Newspaper, profile: Store, insights: BarChart3, seo: Search,
  getReviews: QrCode, autoReply: Bot, activity: Activity, settings: Settings, guide: BookOpen,
};
const RAIL_KEY = 'autoappy.sidebarRail';
const CURRENT_APP = process.env.REACT_APP_CURRENT_APPLICATION || 'auto-appy-google-my-business';
const readRail = () => { try { return window.localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; } };

function usePopover(width = 300) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const toggle = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      const left = Math.min(rect.right + 10, window.innerWidth - width - 12);
      const top = rect.top < window.innerHeight / 2 ? Math.max(12, rect.top) : null;
      setPosition(top === null ? { left: Math.max(12, left), bottom: Math.max(12, window.innerHeight - rect.bottom) } : { left: Math.max(12, left), top });
    }
    setOpen((value) => !value);
  }, [width]);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => { if (!panelRef.current?.contains(event.target) && !anchorRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') { setOpen(false); anchorRef.current?.focus(); } };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return { open, setOpen, toggle, position, anchorRef, panelRef };
}

const roleLabel = (role) => ({ owner: 'Owner', admin: 'Admin' }[role] || 'Member');

export default function GbpSidebar({ mobileOpen, setMobileOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { organisations, organisation, organisationId, chooseOrganisation, link, boot } = useWorkspace();
  const [rail, setRail] = useState(readRail);
  const products = usePopover(300);
  const account = usePopover(260);
  const orgMenu = usePopover(300);
  const activeId = activeItemId(location.pathname);
  const apps = autoAppySwitcherApps(user?.apps);
  const name = user?.name || 'Your account';
  const showRail = rail && !mobileOpen;
  const counts = boot?.counts || {};

  useEffect(() => { try { window.localStorage.setItem(RAIL_KEY, rail ? '1' : '0'); } catch { /* private mode */ } }, [rail]);
  const closeMobile = () => setMobileOpen(false);

  const item = (entry) => {
    const Icon = ICONS[entry.icon] || House;
    const badge = entry.badge ? counts[entry.badge] : 0;
    return (
      <Link key={entry.id} to={link(entry.to)} className={`ws-sb-item${activeId === entry.id ? ' is-active' : ''}`}
        aria-current={activeId === entry.id ? 'page' : undefined} title={showRail ? entry.label : undefined} aria-label={showRail ? entry.label : undefined} onClick={closeMobile}>
        <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
        <span className="ws-sb-text">{entry.label}</span>
        {badge > 0 && <span className={`mx-sb-badge${entry.soft ? ' mx-sb-badge--soft' : ''}`} aria-label={`${badge} ${{ rules_on: 'on', scheduled_posts: 'scheduled' }[entry.badge] || 'waiting'}`}>{badge > 99 ? '99+' : badge}</span>}
      </Link>
    );
  };

  return (
    <>
      <button type="button" className="ws-hamburger" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileOpen} aria-controls="gbp-sidebar">
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
      {mobileOpen && <div className="ws-scrim" onClick={closeMobile} aria-hidden="true" />}
      <div id="gbp-sidebar" className={`ws-sidebar-shell${mobileOpen ? ' is-open' : ''}`}>
        <nav className={`ws-sidebar${showRail ? ' is-rail' : ''}`} aria-label="Main navigation">
          <div className="ws-sb-top">
            <img src={sidebarMark} alt="" />
            <span className="ws-sb-brand">Auto <span>Appy</span></span>
            <span className="mx-sb-product">GMB</span>
            <button type="button" className="ws-sb-iconbtn" onClick={() => setRail((v) => !v)} aria-label={showRail ? 'Expand sidebar' : 'Collapse sidebar'} title={showRail ? 'Expand sidebar' : 'Collapse sidebar'}>
              {showRail ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
            </button>
          </div>

          <button type="button" ref={orgMenu.anchorRef} className="ws-sb-org" onClick={orgMenu.toggle} aria-haspopup="dialog" aria-expanded={orgMenu.open}
            title={showRail ? (organisation?.name || 'Organisation') : undefined} aria-label={`Organisation: ${organisation?.name || 'none'}. Switch organisation`}>
            <span className="ws-sb-org-mark" aria-hidden="true">{autoAppyAppInitials(organisation?.name || 'O').slice(0, 1)}</span>
            <span className="ws-sb-org-text ws-grow" style={{ minWidth: 0 }}>
              <span className="ws-sb-org-name" style={{ display: 'block' }}>{organisation?.name || 'Choose organisation'}</span>
              <span className="ws-sb-org-sub">{organisation ? `Organisation · ${roleLabel(organisation.role)}` : `${organisations.length} available`}</span>
            </span>
            <ChevronsUpDown className="ws-sb-chevron" size={14} color="#5B7180" aria-hidden="true" />
          </button>

          <Link to={link('/posts/new')} className="ws-btn ws-btn--primary mx-sb-create" onClick={closeMobile} title={showRail ? 'New post' : undefined} aria-label={showRail ? 'New post' : undefined}>
            <Plus size={16} aria-hidden="true" /><span className="ws-sb-text">New post</span>
          </Link>

          <div className="ws-sb-scroll">
            {NAV_GROUPS.map((group) => (
              <div className="ws-sb-group" key={group.id} role="group" aria-label={group.label || 'Daily'}>
                {group.label && <div className="ws-sb-label">{group.label}</div>}
                {group.items.map(item)}
              </div>
            ))}
          </div>

          <div className="ws-sb-footer">
            {FOOTER_ITEMS.map(item)}
            <button type="button" ref={products.anchorRef} className="ws-sb-item" onClick={products.toggle} aria-haspopup="dialog" aria-expanded={products.open} title={showRail ? 'Products' : undefined} aria-label={showRail ? 'Products' : undefined}>
              <span className="ws-sb-dots" aria-hidden="true"><i style={{ background: '#147DB5' }} /><i style={{ background: '#7CC4E8' }} /><i style={{ background: '#1F9D57' }} /><i style={{ background: '#183F5A' }} /></span>
              <span className="ws-sb-text">Products</span>
              <ArrowUpRight className="ws-sb-meta" size={14} aria-hidden="true" />
            </button>
            <button type="button" ref={account.anchorRef} className="ws-sb-me" onClick={account.toggle} aria-haspopup="menu" aria-expanded={account.open} aria-label={`Account: ${name}`}>
              <Monogram name={name} size={32} />
              <span className="ws-sb-org-text ws-grow" style={{ minWidth: 0 }}>
                <span className="ws-sb-org-name" style={{ display: 'block' }}>{name}</span>
                <span className="ws-sb-org-sub mx-ellipsis" style={{ display: 'block' }}>{user?.email || 'Profile and sign out'}</span>
              </span>
              <ChevronsUpDown className="ws-sb-chevron" size={14} color="#5B7180" aria-hidden="true" />
            </button>
          </div>
        </nav>
      </div>

      {orgMenu.open && orgMenu.position && (
        <div ref={orgMenu.panelRef} className="ws-popover ws-overlay" role="dialog" aria-label="Switch organisation" style={orgMenu.position}>
          <div className="ws-popover-title">Switch organisation</div>
          {organisations.map((org) => (
            <button key={org.id} type="button" className="ws-menu-item" aria-current={org.id === organisationId ? 'true' : undefined}
              onClick={() => { orgMenu.setOpen(false); closeMobile(); if (org.id !== organisationId) chooseOrganisation(org.id); }}>
              <span className="ws-sb-org-mark" aria-hidden="true">{autoAppyAppInitials(org.name).slice(0, 1)}</span>
              <span className="ws-grow mx-ellipsis">{org.name}</span>
              <span className="ws-chip">{roleLabel(org.role)}</span>
              {org.id === organisationId && <Check size={15} color="#147DB5" aria-hidden="true" />}
            </button>
          ))}
          <div className="ws-menu-sep" />
          <a className="ws-menu-item" href={autoAppyUrl(`/organisation${organisationId ? `?organisation=${organisationId}` : ''}`)}>
            <Users size={16} aria-hidden="true" /><span className="ws-grow">Manage team and access</span><ArrowUpRight size={14} aria-hidden="true" />
          </a>
        </div>
      )}

      {products.open && products.position && (
        <div ref={products.panelRef} className="ws-popover ws-overlay" role="dialog" aria-label="Auto Appy products" style={products.position}>
          <div className="ws-popover-title">Auto Appy products</div>
          {apps.map((app) => {
            const current = app.key === CURRENT_APP;
            const mark = <span className="ws-product-icon" style={{ background: productTone(app), fontSize: productMark(app).length > 2 ? 9 : 11 }} aria-hidden="true">{productMark(app)}</span>;
            return current ? (
              <button key={app.key} type="button" className="ws-menu-item" aria-current="true" onClick={() => products.setOpen(false)}>
                {mark}<span className="ws-grow">{app.name}</span><Check size={15} color="#147DB5" aria-hidden="true" />
              </button>
            ) : (
              <a key={app.key} className="ws-menu-item" href={autoAppyAppLaunchUrl(app, organisationId)}>
                {mark}<span className="ws-grow">{app.name}</span><ArrowUpRight size={14} aria-hidden="true" />
              </a>
            );
          })}
          {!apps.length && <p className="ws-muted" style={{ padding: '4px 10px 10px' }}>No other products are turned on for you yet.</p>}
        </div>
      )}

      {account.open && account.position && (
        <div ref={account.panelRef} className="ws-popover ws-overlay" role="menu" aria-label="Account" style={{ ...account.position, width: 260 }}>
          <div style={{ padding: '8px 10px 10px' }}>
            <div className="ws-sb-org-name">{name}</div>
            <div className="ws-sb-org-sub mx-ellipsis">{user?.email}</div>
          </div>
          <a className="ws-menu-item" role="menuitem" href={autoAppyUrl('/settings/profile')}>
            <CircleUserRound size={16} aria-hidden="true" /><span className="ws-grow">Profile</span><ArrowUpRight size={14} aria-hidden="true" />
          </a>
          <button type="button" className="ws-menu-item" role="menuitem" onClick={async () => { account.setOpen(false); try { await signOut(); } finally { navigate('/'); } }}>
            <LogOut size={16} aria-hidden="true" /><span className="ws-grow">Sign out</span>
          </button>
        </div>
      )}
    </>
  );
}
